import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { colors, WhiteCloseIcon } from '../../styles';

// Shared by saved photos, upload tiles and the lifted tile in a drag.
export const tileCss = css`
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 8px;

  & > button {
    position: absolute;
    top: -8px;
    right: -8px;
    z-index: 1;
    padding: 6px;
    margin: 0;
    background-color: ${colors.primaryButton};
    border: 4px solid white;
    border-radius: 30px;

    svg {
      display: block;
      width: 12px;
      height: 12px;
      fill: white;
    }
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const Tile = styled.div`
  ${tileCss}
  background-color: ${colors.lightBackground};
  /* No "Save image" menu or text selection on a long press: it would stop the drag. */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;

  & > .cover {
    position: absolute;
    top: 6px;
    left: 6px;
    padding: 2px 8px;
    border-radius: 999px;
    background-color: rgba(0, 0, 0, 0.55);
    color: white;
    font-size: 11px;
    font-weight: 500;
    line-height: 16px;
    pointer-events: none;
  }

  &.sortable {
    cursor: grab;
    touch-action: manipulation;

    @media (hover: hover) {
      &:hover {
        translate: 0 -2px;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
      }
    }
  }

  /* The empty place a lifted photo will drop into. */
  &.placeholder {
    box-shadow: none;
    translate: none;

    & > img,
    & > button,
    & > .cover {
      visibility: hidden;
    }
  }

  &.lifted {
    cursor: grabbing;
    transform: scale(1.04);
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.22), 0 6px 10px rgba(0, 0, 0, 0.12);
    animation: lift 0.15s ease-out;
  }

  @keyframes lift {
    from {
      transform: scale(1);
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    translate: none !important;
  }
`;

export const photoAlt = (index, total) => `Photo ${index + 1} of ${total}${index === 0 ? ', cover' : ''}`;

const stop = (e) => e.stopPropagation();

export const PhotoTile = forwardRef(({ src, index, total, onRemove, className, ...rest }, ref) => (
  <Tile ref={ref} className={className} onContextMenu={(e) => e.preventDefault()} {...rest}>
    {onRemove && (
      <button
        type="button"
        // A press on the X must never start a drag.
        onMouseDown={stop}
        onTouchStart={stop}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
      >
        <WhiteCloseIcon />
      </button>
    )}
    <img src={src} alt={photoAlt(index, total)} draggable={false} />
    {index === 0 && (
      <span className="cover" aria-hidden="true">
        Cover
      </span>
    )}
  </Tile>
));

// Shown until the drag code loads: the same tiles, not yet movable.
export const StaticPhotos = ({ photos, srcFor, onRemove }) =>
  photos.map((photo, index) => (
    <PhotoTile key={photo.url} src={srcFor(photo)} index={index} total={photos.length} onRemove={() => onRemove(photo)} />
  ));
